import { SmartButton } from '@tunasong/icons'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { IconProps } from '@tunasong/icons'
import {
  AccountTree,
  Activities,
  Brain as AgentIcon,
  Close,
  Context,
  Debug,
  Folder,
  MoreVertIcon,
  Share as Graph,
  Pool,
  Settings,
  Suggestion,
} from '@tunasong/icons'
import { isCollabEntity, isNote, isPersistedEntity, logger, matchAny } from '@tunasong/models'
import { ChatBadge, useChat } from '@tunasong/plugin-chat/ui'
import { features, useThunkDispatch } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isChat, isFolder, isPage, isSong } from '@tunasong/schemas'
import { useTunaMediaQuery, type SidebarHandler } from '@tunasong/ui-lib'
import {
  HBox,
  ListItemIcon,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  isDevelopment,
  useElementRef,
  useSidebar,
} from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, HTMLAttributes, KeyboardEvent } from 'react'
import { useEffect, useId, useRef } from 'react'
import invariant from 'tiny-invariant'
import sidebarStyles from './sidebar.module.css'
import { SidebarActivities } from './sidebar-activities.js'
import { SidebarAgents } from './sidebar-agents.js'
import { SidebarAssistPanel } from './sidebar-assist-panel.js'
import { SidebarChat } from './sidebar-chat.js'
import { SidebarContent } from './sidebar-content.js'
import { SidebarContext } from './sidebar-context.js'
import { SidebarDebug } from './sidebar-debug.js'
import { SidebarEntity } from './sidebar-entity.js'
import { SidebarEvents } from './sidebar-events.js'
import { SidebarGraph } from './sidebar-graph.js'
import { SidebarPool } from './sidebar-pool.js'
import { SidebarSettings } from './sidebar-settings.js'
import { EntityNotes, NotesBadge } from '@tunasong/editor'
import type { Note } from '@tunasong/models'
import { resolveRefTool } from './resolve-ref-tool.js'
import { shouldHandleRef } from './should-handle-ref.js'
import { getRailLabel, getToolDescription, getToolLabel, partitionSidebarTools } from './sidebar-tool-groups.js'

export interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
  className?: string
  entity?: Persisted<Entity>
}

const Sidebar: FC<SidebarProps> = props => {
  const { entity, className, style, ...restProps } = props

  const { activeTool, setActiveTool } = useSidebar()

  const { activeComments, isLoading: isChatLoading } = useChat({ element: entity })

  // Personal notes: the My Notes badge count, and `?ref=<noteId>` resolution below. Shares the
  // RTK Query cache with EntityNotes, so opening the panel does not refetch.
  const { entities: notes } = graphHooks.useEntityChildren<Note>({ parentId: entity?.id, filter: 'note' })

  /**
   * `?ref=<id>` in the URL opens Chat for a comment, or My Notes focused on one of this entity's
   * notes (a note's backlink, GH #1579). Each ref is handled once: the lists refetch, and a
   * re-run must not re-open a note the user has since collapsed. The guard is set only when a
   * tool resolves, so a ref that arrives before its list has loaded fires once the list does.
   */
  const ref = useElementRef()
  const dispatch = useThunkDispatch()
  const handledRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!shouldHandleRef({ ref, handled: handledRef.current })) {
      return
    }
    const tool = resolveRefTool({
      ref,
      commentIds: activeComments.map(c => c.id),
      noteIds: notes.map(n => n.id),
    })
    if (!(tool && ref)) {
      return
    }
    handledRef.current = ref
    setActiveTool(tool)
    if (tool === 'My Notes') {
      dispatch(features.ui.actions.setPendingNoteScroll(true))
      dispatch(features.ui.actions.setPendingAnchoredNoteId(ref))
    }
  }, [activeComments, dispatch, notes, ref, setActiveTool])

  /** If activeComments change, we automatically open the chat tool */
  const prevActiveComments = useRef<number>(0)
  useEffect(() => {
    if (prevActiveComments.current > 0 && prevActiveComments.current < activeComments.length && !isChatLoading) {
      logger.debug('Setting active', prevActiveComments, activeComments)
      setActiveTool('Chat')
    }
    prevActiveComments.current = activeComments.length
  }, [activeComments, isChatLoading, setActiveTool])

  // 250ms balances two concerns:
  //  - Free-text fields (ElementForm Name/Description) coalesce per-keystroke
  //    bursts so we don't fire one API call per character.
  //  - Discrete-choice fields (ScaleSelect, MeterSelect, EnumSelect type)
  //    propagate to the editor (rootElement.scale → chord suggestions) within
  //    a normal interaction frame, not seconds later.
  // Previously 3s (from e06d8637b "Debounce tweaks", 2023); that left the
  // InlineCaption Roman-numeral parenthetical racing the playwright timeout
  // and going missing entirely on the live UI for ~3s after every scale
  // change (GH #1362).
  const handleUpdate = graphHooks.useEntityUpdate({ debounceDelay: 250 })
  const handleEntityChange = (partial: Partial<Entity>) => {
    invariant(isPersistedEntity(entity), 'Entity to be updated must be an element')
    handleUpdate(entity.id, partial)
  }

  const numComments = activeComments.length
  const ChatIcon = (props: IconProps) => <ChatBadge numComments={numComments} {...props} />

  const numNotes = notes.length
  const NotesIcon = (props: IconProps) => <NotesBadge count={numNotes} {...props} />
  /** The count each rail badge shows, so the button's name can say it too */
  const railCounts: Record<string, number> = { Chat: numComments, 'My Notes': numNotes }

  const tools: SidebarHandler[] = [
    {
      name: `Settings`,
      Icon: Settings,
      Component: SidebarSettings,
      disabled: () => false,
    },
    {
      name: 'Chat',
      Icon: ChatIcon,
      Component: SidebarChat,
      disabled: () => isChat(entity),
    },
    {
      name: 'Activities',
      description: 'Summary of recent activities',
      Icon: Activities,
      Component: SidebarActivities,
      disabled: () => false,
    },
    {
      name: 'Agents',
      description:
        'Chat with the agents attached to this entity. Accept or reject their proposals from the same panel.',
      Icon: AgentIcon,
      Component: SidebarAgents,
      // Mirrors the agent plugin's supportedTypes set (song / page / folder). The
      // SidebarAgents body renders an empty state when the cache is cold or no
      // agents are attached, covering the children-not-yet-loaded window.
      disabled: (entity: Entity) => !matchAny(isSong, isPage, isFolder)(entity),
    },
    {
      name: 'My Notes',
      description:
        'Personal notes — only you can see them. Capo positions, tricky transitions, lyric cues. Notes are also surfaced in performance mode.',
      Icon: NotesIcon,
      Component: EntityNotes,
      // Notes of a note are not a thing (GH #1579)
      disabled: (entity: Entity) => isNote(entity),
    },
    {
      name: 'Event Commands',
      description:
        'Commands to run for different events. For example, you can run a command when a the page is opened.',
      Icon: SmartButton,
      Component: SidebarEvents,
      disabled: () => false,
    },

    {
      name: 'Pool',
      description: 'The Pool contains media that belongs to this item. Select media to insert it into the page.',
      Icon: Pool,
      Component: SidebarPool,
      disabled: (entity: Entity) => !matchAny(isSong, isPage)(entity),
    },
    {
      name: 'Context',
      description: 'The context shows the musical context. If you are in an editor your position will matter.',
      Icon: Context,
      Component: SidebarContext,
      disabled: (entity: Entity) => !isSong(entity),
    },
    {
      // Shown as "Assist" on the rail: suggestions plus the musical context
      name: 'Suggestions',
      description: 'Suggestions for chords, notes or scales, and the musical context they come from',
      Icon: Suggestion,
      Component: SidebarAssistPanel,
      disabled: (entity: Entity) => !isSong(entity),
    },

    // {
    //   name: 'Record',
    //   description: 'Record your performance',
    //   Icon: Microphone,
    //   Component: SidebarRecord,
    //   // @todo move these into the plugins
    //   disabled: (entity: Entity) => !(isSong(entity) || isPage(entity) || isFolder(entity)),
    // },
    // {
    //   name: 'Guitar',
    //   description: 'Guitar tools',
    //   Icon: Guitar,
    //   Component: SidebarGuitar,
    //   disabled: (entity: Entity) => !isSong(entity),
    // },
    isDevelopment()
      ? {
          name: 'Debug',
          description: 'Debug information',
          Icon: Debug,
          Component: SidebarDebug,
          disabled: (entity: Entity) => !isCollabEntity(entity),
        }
      : null,
    isDevelopment()
      ? {
          name: 'Graph',
          description: 'Show the outgoing and incoming links in the content graph.',
          Icon: Graph,
          Component: SidebarGraph,
          disabled: (entity: Entity) => !entity,
        }
      : null,
    isDevelopment()
      ? {
          name: 'Debug Content',
          description: 'All child content',
          Icon: Folder,
          Component: SidebarContent,
          disabled: () => false,
        }
      : null,
    isDevelopment()
      ? {
          name: 'Entity Content',
          description: 'Entity Details',
          Icon: AccountTree,
          Component: SidebarEntity,
          disabled: () => false,
        }
      : null,
  ].filter(Boolean) as SidebarHandler[]

  /** When the entity is not set we collapse the toolbar */
  useEffect(() => {
    if (!entity) {
      setActiveTool(null)
      return
    }
    /** If there is an active tool, and it's disabled, we also collapse */
    if (activeTool && tools.find(t => t.name === activeTool)?.disabled(entity)) {
      setActiveTool(null)
    }
  }, [activeTool, entity, setActiveTool, tools])

  /** Rail buttons by tool name, and the More trigger: where focus returns when a panel closes */
  const railButtons = useRef(new Map<string, HTMLButtonElement>())
  const moreButton = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const titleId = useId()

  /**
   * Opened by the user (rail or More): focus moves to the panel title. A panel that opens by itself
   * (a new comment, a `?ref=` link) leaves focus where the musician is working.
   */
  const focusTitleOnOpen = useRef(false)
  useEffect(() => {
    if (activeTool && focusTitleOnOpen.current) {
      focusTitleOnOpen.current = false
      titleRef.current?.focus()
    }
  }, [activeTool])

  const openTool = (name: SidebarHandler['name']) => {
    focusTitleOnOpen.current = true
    setActiveTool(name)
  }

  const toggleActive = (name: SidebarHandler['name']) => () =>
    activeTool === name ? setActiveTool(null) : openTool(name)

  const { primary: railTools, more: moreTools } = partitionSidebarTools(tools, entity)
  const moreActive = moreTools.some(t => t.name === activeTool)

  /** Close from the panel itself (close button, Esc): focus goes back to the button that owns the panel */
  const closePanel = () => {
    const owner = activeTool ? (railButtons.current.get(activeTool) ?? moreButton.current) : null
    setActiveTool(null)
    owner?.focus()
  }

  const handlePanelKeyDown = (ev: KeyboardEvent<HTMLElement>) => {
    // Portalled popovers bubble through React but are not in the panel's DOM; a menu closing itself
    // marks the event handled. Neither closes the panel.
    if (ev.key !== 'Escape' || ev.defaultPrevented || !panelRef.current?.contains(ev.target as Node)) {
      return
    }
    ev.preventDefault()
    closePanel()
  }

  const xl = useTunaMediaQuery('(min-width:1536px)')
  const tiny = useTunaMediaQuery('(max-width:599.95px)')

  const handler = activeTool !== null ? tools.find(t => t.name === activeTool) : null
  const ActiveComponent = handler ? handler.Component : null
  const showSidebarIcons = !tiny || (tiny && ActiveComponent)

  // 100vw is a hack to make the sidebar full width on mobile
  const containerWidth = tiny && activeTool ? '100vw' : 'auto'

  const toolWidth = activeTool ? (xl ? 450 : 350) : 0

  const label = handler ? getToolLabel(handler.name) : ''
  const description = handler ? (getToolDescription(handler.name) ?? handler.description) : undefined

  return (
    <HBox
      className={cn(sidebarStyles.container, className)}
      style={{
        ...style,
        width: containerWidth,
      }}
      {...restProps}
    >
      <div className={sidebarStyles.panelFrame} style={{ width: toolWidth }}>
        {handler && ActiveComponent && entity ? (
          <aside
            key={entity.id}
            ref={panelRef}
            className={sidebarStyles.panel}
            aria-labelledby={titleId}
            onKeyDown={handlePanelKeyDown}
          >
            <div className={sidebarStyles.panelHeader}>
              <div className={sidebarStyles.panelHeading}>
                <h2 id={titleId} ref={titleRef} tabIndex={-1} className={sidebarStyles.panelTitle}>
                  {label}
                </h2>
                {description ? <p className={sidebarStyles.panelDescription}>{description}</p> : null}
              </div>
              <button
                type="button"
                className={sidebarStyles.closeButton}
                aria-label={`Close ${label}`}
                title="Close (Esc)"
                onClick={closePanel}
              >
                <Close size={18} />
              </button>
            </div>
            <div className={sidebarStyles.panelBody}>
              <ActiveComponent key={entity.id} entity={entity} onEntityChange={handleEntityChange} />
            </div>
          </aside>
        ) : null}
      </div>
      {showSidebarIcons ? (
        <nav className={sidebarStyles.rail} aria-label="Song panels" data-rail>
          {railTools.map(({ name, disabled, Icon }) => (
            <button
              key={name}
              ref={el => {
                if (el) {
                  railButtons.current.set(name, el)
                } else {
                  railButtons.current.delete(name)
                }
              }}
              type="button"
              className={sidebarStyles.railButton}
              disabled={!entity || disabled(entity)}
              title={name}
              aria-label={getRailLabel(name, railCounts[name] ?? 0)}
              aria-pressed={activeTool === name}
              onClick={toggleActive(name)}
            >
              <Icon size={20} />
            </button>
          ))}
          {moreTools.length > 0 ? (
            <>
              <span className={sidebarStyles.railDivider} aria-hidden="true" />
              <Menu>
                <MenuTrigger
                  render={
                    <button
                      ref={moreButton}
                      type="button"
                      className={sidebarStyles.railButton}
                      title="More panels"
                      aria-label="More panels"
                      aria-pressed={moreActive}
                    >
                      <MoreVertIcon size={20} />
                    </button>
                  }
                />
                <MenuContent>
                  {moreTools.map(({ name, Icon }) => (
                    <MenuItem key={name} onClick={() => openTool(name)}>
                      <ListItemIcon>
                        <Icon fontSize={'small'} />
                      </ListItemIcon>
                      {getToolLabel(name)}
                    </MenuItem>
                  ))}
                </MenuContent>
              </Menu>
            </>
          ) : null}
        </nav>
      ) : null}
    </HBox>
  )
}

export default Sidebar
